import { useTranslation } from 'react-i18next';

import { ingestStatusTone } from '../../constants/status-tones';
import type { DeliveryDestination } from '../../hooks/use-destination-copy';
import { humanizeEnum } from '../../utils/format-enum';

import { DeliveryStatusChip } from './delivery-status-chip';

/**
 * Chip for a per-product `ingestStatus` (Meta, and GMC since BE PR #703). Copy
 * follows each contract's guidance — `UPLOADED` reads "Sent to Meta / Google —
 * awaiting confirmation", never "live"; GMC's `PERSISTED` reads "Live in
 * Merchant Center" (ingestion only — approval is the separate `syncStatus`
 * badge); the empty string reads "Not ingested" (nothing happened this run,
 * not an error). Unknown values fall back to a humanised raw string.
 */
export function IngestStatusChip({
  status,
  destination = 'meta',
  className,
  testId = 'delivery-ingest-status-chip',
}: {
  status: string;
  /** Whose wording to use — GMC rows name Google / Merchant Center. */
  destination?: DeliveryDestination;
  className?: string;
  /** `data-testid` for the chip (default `delivery-ingest-status-chip`). */
  testId?: string;
}) {
  const { t } = useTranslation('deliveries');
  const key = status === '' ? 'NONE' : status;
  const fallback = t(`ingest.status.${key}`, { defaultValue: humanizeEnum(status || 'NONE') });
  const label =
    destination === 'gmc' ? t(`ingest.gmcStatus.${key}`, { defaultValue: fallback }) : fallback;
  return (
    <DeliveryStatusChip
      tone={ingestStatusTone(status)}
      label={label}
      testId={testId}
      {...(className ? { className } : {})}
    />
  );
}
